'use client';
import { IconPalette } from '@tabler/icons-react';

import {
  Popover,
  SegmentedControl,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';

import { useEffect, useSyncExternalStore } from 'react';
import { usePathname } from 'next/navigation';
import { IconCheck } from '@tabler/icons-react';
import {
  DEFAULT_KIT_ACCENT,
  KIT_ACCENTS,
  findKitAccentByHex,
  reconcileAccent,
  type KitAccent,
} from '@/lib/media-kit/accent-palette';
import { updateMediaKitAccent } from '@/app/actions/media-kit-accent';

/**
 * Client side of the accent theming system (palette lives in
 * lib/media-kit/accent-palette.ts; server persistence in
 * app/actions/media-kit-accent.ts).
 *
 * Precedence:
 * 1. Share views render a locked server override (<style data-brand-accent-lock>)
 *    from the creator's profile — the viewer's own cache never applies there.
 * 2. Logged-in users: the profile value wins; AccentServerSync pulls it into
 *    the local cache (or pushes a pre-signup local choice up to the server).
 * 3. Otherwise the localStorage cache applies (demo playground, logged out).
 */

const STORAGE_KEY = 'owlscran-kit-accent';

const accentListeners = new Set<() => void>();

function subscribeToAccent(listener: () => void): () => void {
  accentListeners.add(listener);
  return () => accentListeners.delete(listener);
}

/** Raw cache value: null when the visitor never picked an accent. */
function getStoredAccentHexRaw(): string | null {
  return window.localStorage.getItem(STORAGE_KEY);
}

function getStoredAccentHex(): string {
  return getStoredAccentHexRaw() ?? DEFAULT_KIT_ACCENT.hex;
}

function storeAccentHex(hex: string): void {
  window.localStorage.setItem(STORAGE_KEY, hex);
  accentListeners.forEach((listener) => listener());
}

// Serialise profile writes so rapid picker clicks can't land out of order.
// Only one request is in flight at a time; the newest requested colour always
// wins, and intermediate clicks are coalesced.
let pendingPersistHex: string | null = null;
let persistInFlight = false;

async function persistAccent(hex: string): Promise<void> {
  pendingPersistHex = hex;
  if (persistInFlight) return;

  persistInFlight = true;
  try {
    while (pendingPersistHex !== null) {
      // Claim the current value; a click during the await sets it again and
      // re-enters the loop, so the newest colour always wins.
      const target: string = pendingPersistHex;
      pendingPersistHex = null;
      await updateMediaKitAccent(target);
    }
  } catch (error) {
    console.error('Failed to save media kit accent:', error);
    pendingPersistHex = null;
  } finally {
    persistInFlight = false;
  }
}

function useStoredAccent(): KitAccent {
  const storedHex = useSyncExternalStore(
    subscribeToAccent,
    getStoredAccentHex,
    () => DEFAULT_KIT_ACCENT.hex,
  );
  return findKitAccentByHex(storedHex) ?? DEFAULT_KIT_ACCENT;
}

function hasServerAccentLock(): boolean {
  return document.querySelector('style[data-brand-accent-lock]') !== null;
}

/**
 * Mounted once in the root layout. Applies the cached accent to <html> on
 * every page — except pages carrying a locked server override (share views),
 * where the creator's stored colour must win over the viewer's cache.
 */
export const BrandAccentApplier = () => {
  const accent = useStoredAccent();
  const pathname = usePathname();

  useEffect(() => {
    const rootStyle = document.documentElement.style;

    if (hasServerAccentLock()) {
      rootStyle.removeProperty('--brand-rgb');
      rootStyle.removeProperty('--brand-2-rgb');
      rootStyle.removeProperty('--brand-fg');
      return;
    }

    rootStyle.setProperty('--brand-rgb', accent.rgb);
    rootStyle.setProperty('--brand-2-rgb', accent.companionRgb);
    rootStyle.setProperty('--brand-fg', accent.fg);
  }, [accent, pathname]);

  return null;
};

/**
 * Mounted on the logged-in dashboard with the profile's stored accent.
 * Reconciles server and local cache: server wins; a local choice made before
 * signing up (e.g. on the demo) is pushed up to the profile once.
 */
export const AccentServerSync = ({ serverAccentHex }: { serverAccentHex: string | null }) => {
  useEffect(() => {
    const decision = reconcileAccent({
      serverHex: serverAccentHex,
      localHex: getStoredAccentHexRaw(),
    });

    if (decision.action === 'pull') {
      storeAccentHex(decision.hex);
    } else if (decision.action === 'push') {
      void persistAccent(decision.hex);
    }
  }, [serverAccentHex]);

  return null;
};

export const MediaKitAccentPicker = ({
  persistToProfile = false,
  label = 'Customise',
}: {
  /**
   * When true (owner dashboard), selections are saved to the user's profile
   * so share links pick them up. The demo picker stays a local playground.
   */
  persistToProfile?: boolean;
  label?: string;
}) => {
  const accent = useStoredAccent();
  const { setColorScheme } = useMantineColorScheme();
  const colorScheme = useComputedColorScheme();

  const setAccent = (next: KitAccent) => {
    storeAccentHex(next.hex);
    if (persistToProfile) {
      void persistAccent(next.hex);
    }
  };

  return (
    <Popover position="bottom-end" shadow="md" withArrow>
      <Popover.Target>
        <button
          type="button"
          aria-label={label === 'Theme' ? 'Theme' : 'Customise media kit'}
          style={{
            background: 'var(--kit-accent-faint)',
            border: '1px solid var(--kit-accent-soft)',
            color: 'var(--text-primary)',
          }}
          className={`flex h-11 shrink-0 items-center justify-center gap-2 rounded-full text-xs font-medium ${label === 'Theme' ? 'px-3' : 'w-11 sm:w-auto sm:px-3'}`}
        >
          <IconPalette className="h-5 w-5" style={{ color: accent.hex }} aria-hidden="true" />
          <span className={label === 'Theme' ? '' : 'hidden sm:inline'}>{label}</span>
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <p className="text-sm font-medium mb-3">Kit colour</p>
        <div className="flex items-center gap-2" role="radiogroup" aria-label="Kit accent colour">
          {KIT_ACCENTS.map((candidate) => {
            const selected = candidate.hex === accent.hex;
            return (
              <button
                key={candidate.hex}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={`${candidate.name} accent`}
                title={candidate.name}
                onClick={() => setAccent(candidate)}
                className={`flex h-7 w-7 items-center justify-center rounded-full border-0 appearance-none transition-transform hover:scale-110 ${
                  selected
                    ? 'ring-2 ring-[color:var(--text-primary)] ring-offset-2 ring-offset-[color:var(--bg-secondary)]'
                    : ''
                }`}
                style={{ backgroundColor: candidate.hex }}
              >
                {selected && <IconCheck className="h-3.5 w-3.5 text-black/70" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
        <p className="text-sm font-medium mt-5 mb-2">Appearance</p>
        <SegmentedControl
          fullWidth
          aria-label="Appearance"
          value={colorScheme}
          onChange={(value) => setColorScheme(value as 'light' | 'dark')}
          data={[
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
          ]}
        />
      </Popover.Dropdown>
    </Popover>
  );
};
